import { useAdReady } from "@/lib/useAdReady";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { HeroSection } from "@/components/HeroSection";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Car, Truck, BookOpen, ArrowRight, Award, Newspaper, Star, Users, Handshake, Video, Monitor, CarTaxiFront, FileText, Briefcase, Bike, ShieldCheck, Search, MessageCircle, BadgeCheck, Compass } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Index = () => {
  useAdReady();

  // Volgorde: Rijscholen, Online Training, Boeken, Motor & Brommer, Code 95, Taxi, CBR, Video, Blog, Reviews, Nieuws, Verenigingen, Vacatures, Partners
  const menuBlocks = [
    {
      title: "Rijscholen",
      description: "Bekijk en vergelijk 9700+ rijscholen in Nederland. Vind de perfecte rijschool bij jou in de buurt.",
      icon: Car,
      buttonText: "Bekijk rijscholen",
      buttonLink: "/rijscholen",
    },
    {
      title: "Online Training",
      description: "Bekijk alle online theorie cursussen en vergelijk prijzen. Vind de beste online training.",
      icon: Monitor,
      buttonText: "Bekijk trainingen",
      buttonLink: "/online-theorie",
    },
    {
      title: "Theorie Boeken",
      description: "Vergelijk theorie boeken en studiemateriaal. Vind het perfecte lesmateriaal voor je examen.",
      icon: BookOpen,
      buttonText: "Bekijk boeken",
      buttonLink: "/theorie-boeken",
    },
    {
      title: "Motor & Brommer",
      description: "Motorrijscholen en brommer opleidingen. Vergelijk opleiders voor AM, A1, A2 en A rijbewijs.",
      icon: Bike,
      buttonText: "Bekijk motorrijscholen",
      buttonLink: "/tweewielers",
    },
    {
      title: "Code 95",
      description: "Informatie over Code 95 nascholing voor beroepschauffeurs. Vind een erkende opleider.",
      icon: Truck,
      buttonText: "Bekijk Code 95",
      buttonLink: "/code95",
    },
    {
      title: "Taxi Opleiding",
      description: "Taxichauffeur worden? Vind CCV-erkende taxi opleiders en vergelijk prijzen.",
      icon: CarTaxiFront,
      buttonText: "Bekijk taxi opleiders",
      buttonLink: "/taxi",
    },
    {
      title: "CBR",
      description: "Alles over het CBR, examens, locaties en praktische tips voor je theorie en praktijkexamen.",
      icon: Award,
      buttonText: "Bekijk CBR info",
      buttonLink: "/cbr",
    },
    {
      title: "Video",
      description: "Bekijk educatieve video's over rijlessen, verkeer en theorie-examens.",
      icon: Video,
      buttonText: "Bekijk video's",
      buttonLink: "/videos",
    },
    {
      title: "Blog",
      description: "Lees persoonlijke verhalen en ervaringen van leerlingen over rijlessen en het CBR.",
      icon: FileText,
      buttonText: "Bekijk blog",
      buttonLink: "/blog",
    },
    {
      title: "Reviews",
      description: "Lees reviews van leerlingen over rijscholen en online theorie aanbieders.",
      icon: Star,
      buttonText: "Bekijk reviews",
      buttonLink: "/reviews",
    },
    {
      title: "Nieuws",
      description: "Blijf op de hoogte van het laatste nieuws over rijscholen, verkeer en het CBR.",
      icon: Newspaper,
      buttonText: "Bekijk nieuws",
      buttonLink: "/nieuws",
    },
    {
      title: "Verenigingen",
      description: "Ontdek brancheverenigingen voor rijscholen en rij-instructeurs in Nederland.",
      icon: Users,
      buttonText: "Bekijk verenigingen",
      buttonLink: "/verenigingen",
    },
    {
      title: "Vacatures",
      description: "Bekijk vacatures in de rijschoolbranche en vind een nieuwe uitdaging.",
      icon: Briefcase,
      buttonText: "Bekijk vacatures",
      buttonLink: "/vacatures",
    },
    {
      title: "Partners",
      description: "Bekijk onze partners en mogelijkheden voor samenwerking.",
      icon: Handshake,
      buttonText: "Bekijk partners",
      buttonLink: "/partners",
    },
  ];

  const usps = [
    {
      icon: ShieldCheck,
      title: "Onafhankelijk & objectief",
      text: "Theorieaanbieders.nl is een onafhankelijk informatieplatform. Wij beoordelen rijscholen en theorie-aanbieders op basis van openbare data, CBR-cijfers en gecontroleerde profielen — zonder commerciële sturing.",
    },
    {
      icon: BadgeCheck,
      title: "9700+ erkende rijscholen",
      text: "Het meest complete overzicht van Nederland: van grote landelijke ketens tot lokale rijscholen om de hoek. Allemaal gecontroleerd, actueel en voorzien van praktische informatie.",
    },
    {
      icon: Award,
      title: "Actueel voor CBR-examen 2026",
      text: "Alle theoriecursussen en lesmaterialen op het platform zijn afgestemd op de meest recente CBR-eisen. Zo bereid je je voor met materiaal dat aansluit op het examen van vandaag.",
    },
    {
      icon: Compass,
      title: "Gratis vergelijken",
      text: "Vergelijk rijscholen, online theorie en boeken kosteloos. Geen verplichtingen, geen verborgen kosten — alleen heldere informatie om de beste keuze voor jouw rijbewijs te maken.",
    },
  ];

  const steps = [
    {
      icon: Search,
      title: "1. Kies een categorie",
      text: "Selecteer waar je naar zoekt: een rijschool in de buurt, een online theoriecursus, lesmateriaal of een Code 95-opleider.",
    },
    {
      icon: BadgeCheck,
      title: "2. Vergelijk aanbieders",
      text: "Bekijk profielen, beoordelingen, locaties en specialisaties. Filter op provincie of stad en vind de aanbieder die bij jouw situatie past.",
    },
    {
      icon: MessageCircle,
      title: "3. Neem direct contact op",
      text: "Stuur kosteloos een aanvraag naar de gekozen rijschool of opleider. Je ontvangt snel een persoonlijk antwoord met informatie en beschikbaarheid.",
    },
  ];

  const faqs = [
    {
      q: "Wat is Theorieaanbieders.nl?",
      a: "Theorieaanbieders.nl is het onafhankelijke informatieplatform van Nederland voor het vergelijken van rijscholen, online theoriecursussen, theorieboeken, motorrijscholen, Code 95-opleiders en taxi-opleidingen. Het platform biedt een actueel overzicht van meer dan 9700 erkende aanbieders en helpt aspirant-bestuurders een weloverwogen keuze te maken op weg naar hun rijbewijs.",
    },
    {
      q: "Hoeveel rijscholen kan ik vergelijken?",
      a: "Op Theorieaanbieders.nl vind je meer dan 9700 rijscholen verspreid over heel Nederland. Het overzicht wordt doorlopend bijgewerkt op basis van openbare bronnen en officiële registraties, zodat je altijd werkt met de meest recente lijst van actieve aanbieders.",
    },
    {
      q: "Wat kost het om rijscholen te vergelijken?",
      a: "Het vergelijken van rijscholen, online theorie en lesmateriaal is volledig kosteloos voor leerlingen. Er zijn geen verborgen kosten, geen verplichte aanmeldingen en geen abonnementen. Je gebruikt het platform gratis om de juiste aanbieder voor jouw rijbewijs te vinden.",
    },
    {
      q: "Hoe vind ik de beste rijschool bij mij in de buurt?",
      a: "Gebruik de zoekfunctie op de rijscholenpagina om te filteren op stad, provincie of postcode. Je ziet vervolgens een overzicht van rijscholen in jouw regio, inclusief locatie, profielinformatie en contactmogelijkheden. Op detailpagina's kun je extra informatie inzien en direct een aanvraag doen.",
    },
    {
      q: "Wat is online theorie en hoe werkt het?",
      a: "Online theorie is een digitale theoriecursus die je via een browser of app volgt. Je leert op eigen tempo de regels, verkeersborden en examenstof die het CBR toetst, vaak met oefenexamens en uitlegvideo's. Theorieaanbieders.nl toont gecontroleerde aanbieders zodat je een training kiest die aansluit op het actuele CBR-examen.",
    },
    {
      q: "Welke theorieboeken zijn aanbevolen?",
      a: "Op de pagina Theorieboeken vind je een geactualiseerd overzicht van het meest gebruikte lesmateriaal voor het rijbewijs B, motor en Code 95. Per boek tonen wij uitgever, doelgroep en geschiktheid voor het examen, zodat je een keuze maakt die past bij jouw leerstijl en niveau.",
    },
    {
      q: "Wat is Code 95?",
      a: "Code 95 is een verplichte aantekening op het rijbewijs van beroepschauffeurs in het goederen- en personenvervoer. Chauffeurs volgen periodiek nascholing bij erkende opleiders om de aantekening geldig te houden. Op Theorieaanbieders.nl vind je een overzicht van Code 95-opleiders en hun cursusaanbod in Nederland.",
    },
    {
      q: "Is mijn data veilig op Theorieaanbieders.nl?",
      a: "Persoonlijke gegevens worden uitsluitend gebruikt om jouw aanvraag te koppelen aan de door jou gekozen rijschool of opleider. Het platform werkt volgens de AVG, gebruikt beveiligde verbindingen en deelt geen gegevens met derden voor commerciële doeleinden. Lees ons privacybeleid voor de volledige toelichting.",
    },
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Categorieën Theorieaanbieders.nl",
    "itemListElement": menuBlocks.map((b, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": b.title,
      "url": `https://theorieaanbieders.nl${b.buttonLink}`,
    })),
  };

  return (
    <>
      <SEOHead
        title="Vergelijk Rijscholen, Online Theorie & Opleiders"
        description="Vergelijk 9700+ rijscholen, online theorie cursussen, theorieboeken en Code 95 opleiders. Vind de beste theorie training voor jouw rijbewijs in Nederland. Klaar voor CBR-examen 2026."
        keywords="theorie aanbieders, rijscholen nederland, online theorie cursus, theorie examen, rijbewijs halen, autorijschool, theorieboeken, CBR examen, slagingspercentage, rijschool vergelijken"
        canonicalUrl="https://theorieaanbieders.nl/"
        ogImage="https://theorieaanbieders.nl/og-image.png"
        jsonLd={[faqJsonLd, itemListJsonLd]}
      />
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pb-20 lg:pb-0">
        <HeroSection />
        
        {/* Waarom Theorieaanbieders.nl */}
        <section className="py-16 bg-muted/30" aria-labelledby="waarom-heading">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 id="waarom-heading" className="text-3xl md:text-4xl font-bold mb-4">
                Waarom Theorieaanbieders.nl?
              </h2>
              <p className="text-foreground/90 text-lg font-medium">
                Het onafhankelijke kennisplatform voor iedereen die in Nederland een rijbewijs wil halen of een beroepsopleiding zoekt.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {usps.map((usp, i) => (
                <Card key={i} className="h-full">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                      <usp.icon className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{usp.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-foreground/80">{usp.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Menu Blocks Section */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {menuBlocks.map((block, index) => (
                <Card key={index} className="flex flex-col h-full hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <block.icon className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{block.title}</CardTitle>
                    <CardDescription className="text-sm line-clamp-3">{block.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="mt-auto pt-0">
                    <Button 
                      className="w-full bg-green-500 hover:bg-blue-600 text-white transition-colors"
                      asChild
                    >
                      <Link to={block.buttonLink}>
                        {block.buttonText}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Hoe werkt het */}
        <section className="py-16 bg-muted/30" aria-labelledby="hoe-werkt-heading">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 id="hoe-werkt-heading" className="text-3xl md:text-4xl font-bold mb-4">
                Zo werkt Theorieaanbieders.nl
              </h2>
              <p className="text-foreground/80 text-lg">
                In drie eenvoudige stappen vergelijk je rijscholen, theoriecursussen en opleiders in heel Nederland.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {steps.map((step, i) => (
                <Card key={i} className="h-full text-center">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <step.icon className="w-7 h-7 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{step.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* SEO intro tekst */}
        <section className="py-16 bg-background" aria-labelledby="over-heading">
          <div className="container mx-auto px-4 max-w-3xl">
            <article className="prose prose-lg max-w-none">
              <h2 id="over-heading" className="text-3xl md:text-4xl font-bold mb-6">
                Over een rijbewijs halen in Nederland
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Een rijbewijs halen is voor veel Nederlanders een belangrijke stap richting zelfstandigheid. Het proces begint met het theorie-examen bij het Centraal Bureau Rijvaardigheidsbewijzen (CBR), gevolgd door rijlessen en een praktijkexamen. Het slagingspercentage en de kosten verschillen sterk per rijschool, regio en gekozen leervorm. Theorieaanbieders.nl brengt dit landschap overzichtelijk in kaart, zodat aspirant-bestuurders een geïnformeerde keuze maken voordat zij zich aanmelden bij een rijschool of theoriecursus.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Het platform bundelt informatie over rijscholen voor het rijbewijs B, motoropleidingen voor categorieën AM, A1, A2 en A, taxichauffeursopleidingen via het CCV en de verplichte Code 95-nascholing voor beroepschauffeurs. Daarnaast vergelijk je online theoriecursussen en theorieboeken die zijn afgestemd op de meest recente CBR-eisen voor het examen van 2026. Door alle aanbieders op één plek samen te brengen, voorkomt Theorieaanbieders.nl dat leerlingen verspreid over tientallen losse websites moeten zoeken.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                De informatie op het platform is gebaseerd op openbare bronnen, officiële registraties en gecontroleerde profielen van rijscholen zelf. Zo ontstaat een actueel beeld van het Nederlandse rijopleidingenaanbod — van grote landelijke ketens tot lokale rijscholen in steden als Amsterdam, Rotterdam, Den Haag, Utrecht, Eindhoven en Groningen. Wie zich oriënteert op een rijbewijs, vindt hier de basis voor een weloverwogen keuze.
              </p>
            </article>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-muted/30" aria-labelledby="faq-heading">
          <div className="container mx-auto px-4 max-w-3xl">
            <div className="text-center mb-12">
              <h2 id="faq-heading" className="text-3xl md:text-4xl font-bold mb-4">
                Veelgestelde vragen
              </h2>
              <p className="text-foreground/80 text-lg">
                Antwoorden op de vragen die leerlingen het vaakst stellen over rijscholen, theorie en het CBR.
              </p>
            </div>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-base font-semibold text-foreground">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-foreground/80 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      </main>
      <Footer />
      <MobileBottomNav />
      
    </div>
    </>
  );
};

export default Index;
